import { getRuntimeConfigValue } from '../config/runtimeConfig';

declare global {
  interface Window {
    google?: any;
    __googleMapsPromise?: Promise<any>;
  }
}

/** Maps JS key from runtime config only (ECS secret or local .env via runtimeConfig). */
export function getGoogleMapsJsApiKey(): string {
  return (getRuntimeConfigValue().REACT_APP_GOOGLE_MAPS_JS_API_KEY || '').trim();
}

export function isGoogleMapsJsConfigured(): boolean {
  return Boolean(getGoogleMapsJsApiKey());
}

/** Load Maps JavaScript API with Places library (idempotent). */
export function loadGoogleMapsJs(): Promise<any> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps requires a browser'));
  }
  if (window.google?.maps?.places) {
    return Promise.resolve(window.google);
  }
  if (window.__googleMapsPromise) {
    return window.__googleMapsPromise;
  }

  const apiKey = getGoogleMapsJsApiKey();
  if (!apiKey) {
    return Promise.reject(new Error('Google Maps JS API key is not configured'));
  }

  window.__googleMapsPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-google-maps="true"]') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google));
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Maps')));
      if (window.google?.maps) {
        resolve(window.google);
      }
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMaps = 'true';
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });

  return window.__googleMapsPromise;
}
